import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Avatar,
  Tabs,
  Tab,
  Typography,
  Divider,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  Edit,
  Save,
  X,
  User,
  Shield,
  Building2,
  Eye,
  EyeOff,
  AlertCircle,
} from 'lucide-react';
import { getAuth } from 'firebase/auth';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import BASE_URL from '../config';
import FullPageLoader from '../Components/UI/FullPageLoader';

function ProfileModern() {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  // General tab state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    firstname: '',
    lastname: '',
    phonenumber: '',
  });

  // Security tab state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    if (!user) return;
    fetchUserData();
  }, [user]);

  const fetchUserData = async () => {
    try {
      const token = await getAuth().currentUser.getIdToken();
      const response = await axios.get(`${BASE_URL}/api/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data;
      setUserData(data);
      setProfileForm({
        firstname: data.name?.split(' ')[0] || '',
        lastname: data.name?.split(' ').slice(1).join(' ') || '',
        phonenumber: data.phonenumber || '',
      });
    } catch (err) {
      console.error('Error fetching user from API:', err);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const token = await getAuth().currentUser.getIdToken();
      await axios.put(
        `${BASE_URL}/api/users/me`,
        profileForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Profile updated successfully');
      setIsEditingProfile(false);
      fetchUserData();
    } catch (err) {
      console.error('Error updating profile:', err);
      toast.error(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    const { currentPassword, newPassword, confirmNewPassword } = passwordForm;

    if (!currentPassword || !newPassword || !confirmNewPassword) {
      toast.error('All fields are required');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSavingPassword(true);
    try {
      const token = await getAuth().currentUser.getIdToken();
      await axios.put(
        `${BASE_URL}/api/users/me/password`,
        passwordForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password changed successfully');
      setPasswordDialogOpen(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
    } catch (err) {
      console.error('Error changing password:', err);
      toast.error(err.response?.data?.error || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return <FullPageLoader message="Loading profile..." />;
  }

  const fullName = userData?.name || 'User';
  const initials = fullName.split(' ').map(n => n.charAt(0).toUpperCase()).join('');

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto', height: '100%', overflow: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Account Settings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage your account settings and security preferences
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab
            icon={<User size={18} />}
            iconPosition="start"
            label="General"
            sx={{ textTransform: 'none', fontWeight: 500 }}
          />
          <Tab
            icon={<Shield size={18} />}
            iconPosition="start"
            label="Security"
            sx={{ textTransform: 'none', fontWeight: 500 }}
          />
          <Tab
            icon={<Building2 size={18} />}
            iconPosition="start"
            label="Organization"
            sx={{ textTransform: 'none', fontWeight: 500 }}
          />
        </Tabs>
      </Box>

      {/* General Tab */}
      {activeTab === 0 && (
        <Card sx={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Profile Information
              </Typography>
              {!isEditingProfile ? (
                <Button
                  startIcon={<Edit size={16} />}
                  onClick={() => setIsEditingProfile(true)}
                  size="small"
                >
                  Edit
                </Button>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setIsEditingProfile(false)}
                    startIcon={<X size={16} />}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                    startIcon={<Save size={16} />}
                  >
                    {savingProfile ? 'Saving...' : 'Save'}
                  </Button>
                </Box>
              )}
            </Box>

            {/* Avatar */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: 'primary.main',
                  fontSize: '1.75rem',
                  fontWeight: 600,
                }}
              >
                {initials}
              </Avatar>
              <Box sx={{ ml: 2 }}>
                <Typography variant="h6">{fullName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {userData?.email}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Profile Fields */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <TextField
                label="First Name"
                value={profileForm.firstname}
                onChange={(e) => setProfileForm({ ...profileForm, firstname: e.target.value })}
                disabled={!isEditingProfile}
                fullWidth
                size="small"
              />
              <TextField
                label="Last Name"
                value={profileForm.lastname}
                onChange={(e) => setProfileForm({ ...profileForm, lastname: e.target.value })}
                disabled={!isEditingProfile}
                fullWidth
                size="small"
              />
              <TextField
                label="Email"
                value={userData?.email || ''}
                disabled
                fullWidth
                size="small"
              />
              <TextField
                label="Phone Number"
                value={profileForm.phonenumber}
                onChange={(e) => setProfileForm({ ...profileForm, phonenumber: e.target.value })}
                disabled={!isEditingProfile}
                fullWidth
                size="small"
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Security Tab */}
      {activeTab === 1 && (
        <Card sx={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Password
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Change your password to keep your account secure
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setPasswordDialogOpen(true)}
            >
              Change Password
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Organization Tab */}
      {activeTab === 2 && (
        <Card sx={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Organization Details
            </Typography>

            {userData?.organisation_name ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Organization
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {userData.organisation_name}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Role
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={userData.role_name || 'No Role Assigned'}
                      color="primary"
                      size="small"
                    />
                    {userData.user_role && (
                      <Typography variant="caption" color="text.secondary">
                        ({userData.user_role})
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Alert severity="info" icon={<AlertCircle size={18} />}>
                  Contact your organization admin to change your role or organization membership.
                </Alert>
              </Box>
            ) : (
              <Alert severity="warning" icon={<AlertCircle size={18} />}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                  Not a member of any organization
                </Typography>
                <Typography variant="body2">
                  You haven't been added to an organization yet. Contact an organization admin to get invited.
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Password Change Dialog */}
      <Dialog 
        open={passwordDialogOpen} 
        onClose={() => setPasswordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Change Password</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            <TextField
              label="Current Password"
              type={showPasswords.current ? 'text' : 'password'}
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                      edge="end"
                      size="small"
                    >
                      {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="New Password"
              type={showPasswords.new ? 'text' : 'password'}
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              fullWidth
              helperText="At least 6 characters"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                      edge="end"
                      size="small"
                    >
                      {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Confirm New Password"
              type={showPasswords.confirm ? 'text' : 'password'}
              value={passwordForm.confirmNewPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirmNewPassword: e.target.value })}
              fullWidth
              error={passwordForm.confirmNewPassword && passwordForm.newPassword !== passwordForm.confirmNewPassword}
              helperText={
                passwordForm.confirmNewPassword && passwordForm.newPassword !== passwordForm.confirmNewPassword
                  ? 'Passwords do not match'
                  : ''
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                      edge="end"
                      size="small"
                    >
                      {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleChangePassword}
            disabled={savingPassword}
          >
            {savingPassword ? 'Changing...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default ProfileModern;
